import { memo, useState } from "react";
import { slugify } from "@/model/theme";
import type { TokenId } from "@/model/tokens";
import { useShownTheme } from "@/store/editor";
import { cn } from "@/ui/kit/cn";
import { type Line, LUA, SAMPLES, type Sample, type Span } from "./samples";
import { C, S, syntaxStyle } from "./tok";
import { v } from "./vars";

/** `themes-<slug>` of the theme on screen (the only part of the preview that reads the store). */
function AppName() {
  const name = useShownTheme().theme.name;
  return <>themes-{slugify(name)}</>;
}

const DIAG: Record<string, { token: TokenId; icon: string }> = {
  error: { token: "ui.error", icon: "●" },
  warning: { token: "ui.warning", icon: "▲" },
  info: { token: "ui.info", icon: "●" },
  hint: { token: "ui.hint", icon: "●" },
};

function SpanView({ span }: { span: Span }) {
  const bg =
    span.search === "current"
      ? v("ui.curSearchBg")
      : span.search === "match"
        ? v("ui.searchBg")
        : span.matchParen
          ? v("ui.matchParen")
          : undefined;
  const style = {
    ...(span.role ? syntaxStyle(span.role) : { color: v("ui.editorFg") }),
    background: bg,
    ...(span.search === "current" ? { color: v("ui.editorBg") } : {}),
    ...(span.undercurl
      ? {
          textDecorationLine: "underline",
          textDecorationStyle: "wavy" as const,
          textDecorationColor: v(span.undercurl === "error" ? "ui.error" : "ui.warning"),
          textUnderlineOffset: "3px",
        }
      : {}),
  };
  const token =
    span.search === "current"
      ? "ui.curSearchBg"
      : span.search === "match"
        ? "ui.searchBg"
        : span.matchParen
          ? "ui.matchParen"
          : span.role
            ? `syntax.${span.role}`
            : "ui.editorFg";
  return (
    <span data-token={token} style={style}>
      {span.text}
    </span>
  );
}

function CodeLine({
  line,
  n,
  cursor,
  visual,
  relative,
}: {
  line: Line;
  n: number;
  cursor: boolean;
  visual: boolean;
  relative: number;
}) {
  const git = line.git
    ? ({ add: "ui.gitAdd", change: "ui.gitChange", delete: "ui.gitDelete" } as const)[line.git]
    : undefined;
  return (
    <div
      className="flex whitespace-pre"
      style={{ background: visual ? v("ui.selection") : cursor ? v("ui.cursorLine") : undefined }}
      data-token={visual ? "ui.selection" : cursor ? "ui.cursorLine" : undefined}
    >
      <span
        className="w-[1.2ch] text-center"
        data-token={git}
        style={{ color: git ? v(git) : undefined }}
      >
        {git ? (line.git === "delete" ? "▁" : "▎") : " "}
      </span>
      <span
        className="w-[3.5ch] pr-[1ch] text-right"
        data-token={cursor ? "ui.lineNrActive" : "ui.lineNr"}
        style={{
          color: v(cursor ? "ui.lineNrActive" : "ui.lineNr"),
          fontWeight: cursor ? 700 : 400,
        }}
      >
        {cursor ? n : relative}
      </span>
      <span className="pl-[0.5ch]">
        {line.spans.map((s, i) => (
          <SpanView key={i} span={s} />
        ))}
        {line.spans.length === 0 && " "}
        {cursor && (
          <span
            style={{ background: v("terminal.cursor"), color: v("terminal.cursorText") }}
            data-token="terminal.cursor"
          >
            {" "}
          </span>
        )}
        {line.diagnostic && (
          <span
            data-token={DIAG[line.diagnostic.level]?.token}
            className="ml-[2ch] rounded-sm px-[0.5ch]"
            style={{
              color: v(DIAG[line.diagnostic.level]?.token ?? "ui.error"),
              background: `color-mix(in oklab, ${v("ui.editorBg")} 88%, ${v(DIAG[line.diagnostic.level]?.token ?? "ui.error")})`,
            }}
          >
            {DIAG[line.diagnostic.level]?.icon} {line.diagnostic.message}
          </span>
        )}
      </span>
    </div>
  );
}

function Popup() {
  const items: [string, string, TokenId][] = [
    ["nvim_set_hl", "Function", "syntax.function"],
    ["nvim_set_hl_ns", "Function", "syntax.function"],
    ["nvim_set_option_value", "Function", "syntax.function"],
    ["nvim_set_var", "Function", "syntax.function"],
    ["nvim_get_hl", "Function", "syntax.function"],
    ["nvim_set_decoration_provider", "Function", "syntax.function"],
  ];
  return (
    <div
      className="absolute z-10 min-w-[34ch] border shadow-lg"
      style={{ background: v("ui.pmenuBg"), borderColor: v("ui.border") }}
      data-token="ui.pmenuBg"
    >
      {items.map(([label, kind, tok], i) => (
        <div
          key={label}
          className="flex justify-between gap-[2ch] px-[1ch]"
          style={{ background: i === 0 ? v("ui.pmenuSel") : undefined }}
          data-token={i === 0 ? "ui.pmenuSel" : "ui.pmenuBg"}
        >
          <span style={{ color: v("ui.editorFg"), fontWeight: i === 0 ? 700 : 400 }}>
            <span data-token="ui.accent" style={{ color: v("ui.accent"), fontWeight: 700 }}>
              nvim_set
            </span>
            {label.slice(8)}
          </span>
          <span data-token={tok} style={{ color: v(tok) }}>
            ƒ {kind}
          </span>
        </div>
      ))}
    </div>
  );
}

function Hover() {
  return (
    <div
      className="absolute top-2 right-3 z-10 w-[44ch] rounded-sm border px-[1ch] py-[0.5ch] shadow-lg"
      style={{ background: v("ui.panelBg"), borderColor: v("ui.border") }}
      data-token="ui.panelBg"
    >
      <div>
        <S k="function">nvim_set_hl</S>
        <S k="punctuation">(</S>
        <S k="parameter">ns_id</S>
        <S k="punctuation">:</S> <S k="type">integer</S>
        <S k="punctuation">,</S> <S k="parameter">name</S>
        <S k="punctuation">:</S> <S k="type">string</S>
        <S k="punctuation">, …)</S>
      </div>
      <div className="mt-[0.5ch]" style={{ color: v("ui.editorFg") }}>
        Sets a highlight group.
      </div>
      <div style={{ color: v("syntax.comment") }} data-token="syntax.comment">
        See{" "}
        <C fg="syntax.link" style={{ textDecoration: "underline" }}>
          :h nvim_set_hl()
        </C>
      </div>
    </div>
  );
}

function Explorer() {
  const rows: [string, number, TokenId, string?][] = [
    ["▾", 0, "ui.accent", "themes"],
    ["▾", 1, "ui.accent", "colors"],
    ["·", 2, "ui.editorFg", "<theme>.lua"],
    ["▾", 1, "ui.accent", "lua"],
    ["▾", 2, "ui.accent", "themes"],
    ["+", 3, "ui.gitAdd", "init.lua"],
    ["~", 3, "ui.gitChange", "palettes.lua"],
    ["·", 1, "ui.editorFg", "README.md"],
    ["·", 1, "ui.lineNr", ".gitignore"],
  ];
  return (
    <div
      className="w-[24ch] shrink-0 border-r py-[0.5ch]"
      style={{ background: v("ui.panelBg"), borderColor: v("ui.border") }}
      data-token="ui.panelBg"
    >
      {rows.map(([icon, depth, tok, name], i) => (
        <div
          key={i}
          className="truncate whitespace-pre"
          style={{
            paddingLeft: `${depth * 2 + 1}ch`,
            background: i === 5 ? v("ui.cursorLine") : undefined,
          }}
        >
          <C fg={tok}>{icon} </C>
          <C fg={tok === "ui.accent" ? "ui.editorFg" : tok}>
            {name === "<theme>.lua" ? (
              <>
                <AppName />
                .lua
              </>
            ) : (
              name
            )}
          </C>
        </div>
      ))}
    </div>
  );
}

function StatusLine({ sample }: { sample: Sample }) {
  return (
    <div
      className="flex whitespace-pre"
      style={{ background: v("ui.statusBg"), color: v("ui.statusFg") }}
      data-token="ui.statusBg"
    >
      <C
        bg="ui.accent"
        style={{ color: v("ui.editorBg"), fontWeight: 700 }}
        className="px-[1ch]"
        token="ui.accent"
      >
        NORMAL
      </C>
      <C fg="ui.statusFg" className="px-[1ch]">
        ⎇ main
      </C>
      <C fg="ui.statusFg" className="px-[1ch]">
        {sample.file}
      </C>
      <C fg="ui.error">✖ 1 </C>
      <C fg="ui.warning">▲ 1</C>
      <span className="flex-1" />
      <C fg="ui.statusFg" className="px-[1ch]">
        {sample.lang}
      </C>
      <C bg="ui.accent" style={{ color: v("ui.editorBg") }} className="px-[1ch]" token="ui.accent">
        {sample.cursor + 1}:5
      </C>
    </div>
  );
}

function TabLine({ sample }: { sample: Sample }) {
  const other = ["init.lua", "tokens.ts"].filter((f) => !sample.file.endsWith(f)).slice(0, 2);
  return (
    <div
      className="flex whitespace-pre"
      style={{ background: v("ui.tabInactiveBg") }}
      data-token="ui.tabInactiveBg"
    >
      <C
        bg="ui.editorBg"
        fg="ui.editorFg"
        className="border-t-2 px-[1.5ch]"
        style={{ borderColor: v("ui.accent"), fontWeight: 700 }}
        token="ui.editorBg"
      >
        {sample.file.split("/").at(-1)}
      </C>
      {other.map((f) => (
        <C key={f} fg="syntax.comment" className="px-[1.5ch]" token="ui.tabInactiveBg">
          {f}
        </C>
      ))}
    </div>
  );
}

export const NeovimPreview = memo(function NeovimPreview({
  compact = false,
}: {
  compact?: boolean;
}) {
  const [sampleId, setSampleId] = useState(LUA.id);
  const sample = SAMPLES.find((s) => s.id === sampleId) ?? LUA;
  const lines = compact ? sample.lines.slice(0, 18) : sample.lines;
  return (
    <div
      className={cn(
        "relative flex h-full flex-col",
        compact ? "text-[11px] leading-[1.45]" : "text-[13px] leading-[1.55]",
      )}
      style={{ background: v("ui.editorBg"), color: v("ui.editorFg") }}
      data-token="ui.editorBg"
    >
      {!compact && (
        <div className="absolute top-1 right-2 z-20 flex gap-1 font-sans">
          {SAMPLES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSampleId(s.id)}
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px]",
                s.id === sampleId ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
      <TabLine sample={sample} />
      <div className="relative flex min-h-0 flex-1">
        {!compact && <Explorer />}
        <div className="relative min-w-0 flex-1 overflow-hidden pt-[0.25ch]">
          {lines.map((line, i) => (
            <CodeLine
              key={i}
              line={line}
              n={i + 1}
              cursor={i === sample.cursor}
              visual={!!sample.visual && i >= sample.visual[0] && i <= sample.visual[1]}
              relative={Math.abs(i - sample.cursor)}
            />
          ))}
          {Array.from({ length: compact ? 0 : 3 }, (_, i) => (
            <div
              key={i}
              style={{ color: v("ui.nonText") }}
              data-token="ui.nonText"
              className="pl-[5ch]"
            >
              ~
            </div>
          ))}
          {sample.popupAt !== undefined && (
            <div
              style={{
                position: "absolute",
                top: `calc(${sample.popupAt + 1} * 1lh + 0.25ch)`,
                left: compact ? "18ch" : "22ch",
              }}
            >
              <Popup />
            </div>
          )}
          {!compact && <Hover />}
        </div>
      </div>
      <StatusLine sample={sample} />
      <div className="whitespace-pre px-[1ch]" style={{ color: v("ui.editorFg") }}>
        :colorscheme <AppName />
      </div>
    </div>
  );
});
